<template>
  <DialogHeader>
    <template #left>
      <div class="cursor-pointer" @click="close()">Cancel</div>
    </template>
    Deactivate account
  </DialogHeader>
  <DialogContent>
    <div class="mb-[18px]">
      <div class="mb-[14px] font-secondary">
        Hey {{ userStore.user?.name }}, we're sorry you're thinking of leaving
        us.
      </div>
      <div class="font-secondary">
        By deactivating your account the following will happen:
      </div>
    </div>
    <div
      class="flex flex-col gap-y-[14px] bg-gray-200/30 px-[22px] py-6 rounded-md"
    >
      <div class="flex">
        <Icon name="Deny" :size="18" class="mr-2" />
        <div class="font-secondary text-gray-600 -mt-px">
          Effective immediately, you will no longer be able to access your
          copyrighted work until you enable your account by logging back in.
        </div>
      </div>
      <div class="flex">
        <Icon name="Deny" :size="18" class="mr-2" />
        <div class="font-secondary text-gray-600 -mt-px">
          Upon deactivating, you cannot add new files for copyright protection.
        </div>
      </div>
      <div class="flex">
        <Icon name="Deny" :size="18" class="mr-2" />
        <div class="font-secondary text-gray-600 -mt-px">
          Your account balance will continue to accrue and this accrued balance
          will need to be paid in full if you want to enable your account again.
        </div>
      </div>
      <div class="flex">
        <Icon name="Deny" :size="18" class="mr-2" />
        <div class="font-secondary text-gray-600 -mt-px">
          Any pending sales, transfers, or licenses will be immediately
          cancelled.
        </div>
      </div>
      <div class="flex">
        <Icon name="Deny" :size="18" class="mr-2" />
        <div class="font-secondary text-gray-600 -mt-px">
          If you have any active paid subscriptions (e.g., Pro) purchased
          through the Dacr app, they will remain active. You can manage these
          subscriptions through the platform where you originally subscribed.
        </div>
      </div>
    </div>

    <div v-if="hasOutstandingBalance" class="mt-4 font-secondary">
      To continue to deactivate your account you must pay
      <span class="font-primary font-semibold -mr-1">
        your outstanding balance of
        {{ outstandingBalance }}
      </span>
      .
    </div>
    <div class="mt-6">
      <UiButton class="w-full!" danger @click="handleSubmit">
        {{ buttonCopy }}
      </UiButton>
    </div>
  </DialogContent>
</template>

<script setup lang="ts">
import { Icon, UiButton } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'

import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'
import { formatPrice } from '@/shared/utils/money'
import { computed } from 'vue'
import { useUserStore } from '@/shared/stores/user'
import { useBillingStore } from '@/features/billing/store'

const { close, open } = useDialog()

const userStore = useUserStore()
const billingStore = useBillingStore()

const hasOutstandingBalance = computed(() =>
  billingStore.billing?.upcomingDetails.upcomingBilling?.breakdown.some(
    (breakdown) => breakdown.outstanding
  )
)

const outstandingBalance = computed(() => {
  const totalOutstanding =
    billingStore.billing?.upcomingDetails.upcomingBilling?.breakdown.reduce(
      (acc, breakdown) =>
        (acc += breakdown.outstanding ? breakdown.amount || 0 : 0),
      0
    ) || 0

  return formatPrice({
    amount: totalOutstanding / 100,
    currency: billingStore.billing?.upcomingDetails.upcomingBilling?.currency,
  })
})

const buttonCopy = hasOutstandingBalance.value
  ? 'Pay and deactivate'
  : 'Deactivate'

const handleSubmit = () => {
  open(Dialogs.ConfirmDeactivateAccount)
}
</script>
